/* ---------- 1. rich-text list → cards (cloned from Webflow-styled templates) ----------
 *
 * Designer contract — one hidden wrapper anywhere on the page:
 *
 *   [data-card-templates]                    div, display:none in Webflow — removed at boot
 *     [data-card-template="stream"]          default stream card (Div — never carries a link)
 *     [data-card-template="stream-link"]     its link variant (Link Block, arrow built in)
 *     [data-card-template="broadcast"]       default broadcast card
 *     [data-card-template="broadcast-link"]  its link variant
 *
 * Variant choice is per item: a bullet with a rich-text <a> clones the "-link" template and
 * copies its href/target/rel (rel gains noopener noreferrer for _blank); otherwise the default
 * is cloned. A missing "-link" template falls back to the default. Build the link variant as
 * the SAME base class + a combo (duplicate the card, add the combo) so styling stays
 * single-source and only the structural delta (the arrow) is duplicated.
 *
 * Per-field looks: the hook attribute's VALUE names a different template set for that field
 * alone — data-stream-broadcast="linear" clones "linear"/"linear-link". Same grammar, new look.
 * An empty value uses the layout's default templates.
 *
 * Slots inside each template (nesting/position is free — structure is 100% Webflow's).
 * Put slot attributes on the innermost TEXT elements — a fill replaces the element's content:
 *   [data-card-title]      receives the bullet's main text
 *   [data-card-detail]     ONE prototype line — cloned once per detail, removed when there are none
 *   [data-card-pill]       removed when the item has no pill — its closest
 *                          [data-card-pill-wrap] ancestor (e.g. a styled badge) goes with it
 *
 * Missing default template → the rich text is left rendered as-is and a warning is logged.
 */

import { $, $$, clone, setAttrs, textOf } from '../lib/dom';
import { warn } from '../lib/log';

interface CardData {
  details: string[];
  pill: string | null;
}
interface Layout {
  template: string;
  extract(li: Element): CardData;
}
interface Variants {
  base: Element;
  linked: Element;
}

/** Prototypes leave the DOM with their hidden wrapper. */
const collectTemplates = () => {
  const map = new Map($$('[data-card-template]').map((n) => [n.getAttribute('data-card-template')?.trim() ?? '', n]));
  $$('[data-card-templates]').forEach((wrap) => wrap.remove());
  return map;
};

const childList = (li: Element) => $(':scope > ul, :scope > ol', li);
const childItems = (li: Element) => {
  const list = childList(li);
  return list ? $$(':scope > li', list) : [];
};
/** Text of an <li> minus its nested list. */
export const ownText = (li: Element) => {
  const nested = childList(li);
  return [...li.childNodes]
    .filter((n) => n !== nested)
    .map(textOf)
    .filter(Boolean)
    .join(' ');
};

/** Layout strategies — which template each hook clones and how nested bullets fill its slots. */
const LAYOUTS: Record<'data-stream-list' | 'data-stream-broadcast', Layout> = {
  // Bullet 3 (if present) becomes the pill; the rest are detail lines.
  'data-stream-list': {
    template: 'stream',
    extract: (li) => {
      const subs = childItems(li).map(textOf);
      const hasPill = subs.length >= 3;
      return { details: hasPill ? subs.filter((_, i) => i !== 2) : subs, pill: hasPill ? (subs[2] ?? null) : null };
    },
  },
  // First bullet is the detail line; its first sub-bullet becomes the pill.
  'data-stream-broadcast': {
    template: 'broadcast',
    extract: (li) => {
      const [sub] = childItems(li);
      const [pillItem] = sub ? childItems(sub) : [];
      return { details: sub ? [ownText(sub)].filter(Boolean) : [], pill: pillItem ? textOf(pillItem) : null };
    },
  },
};

const buildCard = (li: Element, { base, linked }: Variants, { details, pill }: CardData) => {
  const link = $<HTMLAnchorElement>('a[href]', li);
  const card = clone(link ? linked : base); // variant = which template gets cloned
  card.removeAttribute('data-card-template');
  card.classList.remove('hide', 'w-condition-invisible'); // belt-and-braces if the template itself was hidden

  if (link) {
    const target = link.getAttribute('target');
    const rel = link.getAttribute('rel') || (target === '_blank' ? 'noopener noreferrer' : '');
    setAttrs(card, { href: link.getAttribute('href') ?? '', ...(target ? { target } : {}), ...(rel ? { rel } : {}) });
  } else {
    for (const name of ['href', 'target', 'rel']) card.removeAttribute(name); // no-op when the default is a Div
  }

  const title = $('[data-card-title]', card);
  if (title) title.textContent = ownText(li);

  const detailProto = $('[data-card-detail]', card);
  detailProto?.replaceWith(...details.map((t) => Object.assign(clone(detailProto), { textContent: t })));

  const pillSlot = $('[data-card-pill]', card);
  if (pillSlot) {
    if (pill) pillSlot.textContent = pill;
    else (pillSlot.closest('[data-card-pill-wrap]') ?? pillSlot).remove();
  }

  return card;
};

export const renderLists = () => {
  let cache: Map<string, HTMLElement> | undefined;
  const templates = () => (cache ??= collectTemplates());
  Object.entries(LAYOUTS).forEach(([hook, { template: fallback, extract }]) =>
    $$(`[${hook}]`).forEach((root) => {
      const name = root.getAttribute(hook)?.trim() || fallback; // hook VALUE picks this field's template set
      const base = templates().get(name);
      if (!base) return warn(`missing [data-card-template="${name}"] — [${hook}] left as rich text`);
      const variants: Variants = { base, linked: templates().get(`${name}-link`) ?? base }; // no link variant → default serves both
      $$(':scope > ul, :scope > ol', root).forEach(
        (list) => list.replaceWith(...$$(':scope > li', list).map((li) => buildCard(li, variants, extract(li)))), // one mutation per list
      );
    }),
  );
};
